import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Rocket, Plus, ArrowRight, Settings2, Building2, AlertTriangle, CheckCircle2, X, Archive, Trash2, RotateCcw, ShieldAlert, Loader2 } from "lucide-react";

export const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600 ring-slate-200",
  onboarding: "bg-blue-50 text-blue-700 ring-blue-200",
  testing: "bg-amber-50 text-amber-700 ring-amber-200",
  ready_for_go_live: "bg-violet-50 text-violet-700 ring-violet-200",
  live: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  suspended: "bg-rose-50 text-rose-700 ring-rose-200",
};

export function StatusPill({ company }) {
  const key = company.status === "suspended" ? "suspended" : company.onboarding_status;
  return (
    <span
      data-testid={`company-status-${company.slug}`}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${STATUS_STYLES[key] || STATUS_STYLES.draft}`}
    >
      {company.onboarding_status_label}
    </span>
  );
}

function SetupBar({ pct }) {
  const tone = pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-blue-500" : "bg-amber-500";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-600 tabular-nums w-9 text-right">{pct}%</span>
    </div>
  );
}

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 40);
const EMPTY = {
  name: "", trading_name: "", slug: "", company_number: "", vat_number: "", address: "",
  telephone: "", general_email: "", website: "", timezone: "Europe/London", locale: "en-GB",
  currency: "GBP", plan: "trial", initial_admin_email: "",
};

const MODAL_INPUT =
  "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400";

// Hoisted to module scope so it keeps a stable identity across parent re-renders
// (defining it inline would re-mount every input on each keystroke → lost focus).
function TextField({ label, value, onChange, placeholder, type = "text", req, testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}{req && <span className="text-rose-500"> *</span>}</label>
      <input
        value={value || ""} type={type} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={MODAL_INPUT}
      />
    </div>
  );
}

function AddCompanyModal({ meta, onClose, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/platform/templates").then((r) => setTemplates((r.data.templates || []).filter((t) => !t.disabled))).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onNameChange = (v) => setF((p) => ({ ...p, name: v, slug: slugTouched ? p.slug : slugify(v) }));
  const onSlugChange = (v) => { setSlugTouched(true); set("slug", slugify(v)); };

  const submit = async () => {
    setSaving(true);
    try {
      const { name, slug, plan, initial_admin_email, ...cfg } = f;
      const config = {
        legal_name: name, trading_name: cfg.trading_name, company_number: cfg.company_number,
        vat_number: cfg.vat_number, address: cfg.address, telephone: cfg.telephone,
        general_email: cfg.general_email, website: cfg.website, timezone: cfg.timezone,
        locale: cfg.locale, currency: cfg.currency,
      };
      const r = await api.post("/platform/companies", {
        name, slug, plan, enabled_modules: [], config,
        onboarding_status: "draft", initial_admin_email: initial_admin_email || undefined,
        template: f.template || undefined,
      });
      toast.success(f.template ? "Company created from template" : "Company created in Draft");
      onCreated(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 backdrop-blur-sm p-4 sm:p-8" data-testid="add-company-modal">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 my-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Plus className="w-4 h-4 text-white" strokeWidth={2.5} />
            </span>
            <h2 className="text-base font-bold text-slate-900">Add Company</h2>
          </div>
          <button onClick={onClose} data-testid="add-company-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div data-testid="template-picker">
            <label className="block text-xs font-medium text-slate-500 mb-2">Choose Setup</label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              <button type="button" onClick={() => set("template", "")} data-testid="template-card-blank"
                className={`text-left rounded-xl border p-3 transition-colors ${!f.template ? "border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/40" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="text-sm font-semibold text-slate-800">Start Blank</div>
                <div className="text-[11px] text-slate-400 mt-0.5">Configure everything manually from scratch.</div>
              </button>
              {templates.map((t) => (
                <button type="button" key={t.key} onClick={() => set("template", t.key)} data-testid={`template-card-${t.key}`}
                  className={`text-left rounded-xl border p-3 transition-colors ${f.template === t.key ? "border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/40" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>
                  <div className="text-[10px] text-slate-400 mt-1.5 truncate">Modules: {(t.enabled_modules || []).join(", ") || "—"}</div>
                  <div className="text-[10px] text-slate-400 truncate">Integrations: {(t.integrations || []).join(", ") || "—"}</div>
                </button>
              ))}
            </div>
            {f.template && <p className="text-[11px] text-emerald-600 mt-2">Template defaults (modules, integrations, routing) will be applied — no data or credentials are copied.</p>}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Legal company name" value={f.name} onChange={onNameChange} placeholder="Acme Services Ltd" req testId="add-company-name" />
            <TextField label="Trading name" value={f.trading_name} onChange={(v) => set("trading_name", v)} placeholder="Acme" testId="add-company-trading_name" />
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Slug<span className="text-rose-500"> *</span></label>
              <input
                value={f.slug} onChange={(e) => onSlugChange(e.target.value)}
                data-testid="add-company-slug" placeholder="acme"
                className={MODAL_INPUT}
              />
            </div>
            <TextField label="Company number" value={f.company_number} onChange={(v) => set("company_number", v)} placeholder="12345678" testId="add-company-company_number" />
            <TextField label="VAT number" value={f.vat_number} onChange={(v) => set("vat_number", v)} placeholder="GB123456789" testId="add-company-vat_number" />
            <TextField label="Telephone" value={f.telephone} onChange={(v) => set("telephone", v)} placeholder="+44 20 1234 5678" testId="add-company-telephone" />
            <TextField label="General email" value={f.general_email} onChange={(v) => set("general_email", v)} placeholder="hello@acme.co.uk" type="email" testId="add-company-general_email" />
            <TextField label="Website" value={f.website} onChange={(v) => set("website", v)} placeholder="https://acme.co.uk" testId="add-company-website" />
            <TextField label="Timezone" value={f.timezone} onChange={(v) => set("timezone", v)} placeholder="Europe/London" testId="add-company-timezone" />
            <TextField label="Locale" value={f.locale} onChange={(v) => set("locale", v)} placeholder="en-GB" testId="add-company-locale" />
            <TextField label="Currency" value={f.currency} onChange={(v) => set("currency", v)} placeholder="GBP" testId="add-company-currency" />
          </div>
          <TextField label="Address" value={f.address} onChange={(v) => set("address", v)} placeholder="1 High Street, London, EC1A 1AA" testId="add-company-address" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Plan</label>
              <select
                value={f.plan} onChange={(e) => set("plan", e.target.value)} data-testid="add-company-plan"
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm capitalize text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                {(meta?.plans || ["trial", "standard", "enterprise"]).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <TextField label="Initial Super Admin email" value={f.initial_admin_email} onChange={(v) => set("initial_admin_email", v)} placeholder="admin@acme.co.uk" type="email" testId="add-company-initial_admin_email" />
          </div>
          <p className="text-xs text-slate-400">
            The company starts in <span className="font-semibold text-slate-500">Draft</span>. Modules start empty — pick an operating model in Setup to get recommendations. The Super Admin is created using the existing membership model.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            onClick={submit} disabled={saving || !f.name || !f.slug}
            data-testid="add-company-submit"
            className="rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2 transition-colors"
          >
            {saving ? "Creating…" : "Create company"}
          </button>
        </div>
      </div>
    </div>
  );
}

const CLASS_META = {
  safe_to_delete: { label: "Safe to delete", tone: "text-emerald-700 bg-emerald-50 ring-emerald-200" },
  archive_only: { label: "Archive only", tone: "text-amber-700 bg-amber-50 ring-amber-200" },
  needs_review: { label: "Needs review", tone: "text-rose-700 bg-rose-50 ring-rose-200" },
};

function CleanupModal({ company, onClose, onDone }) {
  const [dep, setDep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    api.get(`/platform/companies/${company.id}/dependencies`).then((r) => setDep(r.data)).catch(() => setDep({ error: true }));
  }, [company.id]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const archive = () => run(() => api.post(`/platform/companies/${company.id}/archive`), "Company archived");
  const unarchive = () => run(() => api.post(`/platform/companies/${company.id}/unarchive`), "Company restored");
  const del = () => run(() => api.delete(`/platform/companies/${company.id}`, { data: { confirm_slug: confirmText.trim() } }), "Company deleted");

  const cm = dep && CLASS_META[dep.classification];
  const canDelete = dep && dep.deletable && confirmText.trim().toLowerCase() === (company.slug || "").toLowerCase();

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 backdrop-blur-sm p-4 sm:p-8" data-testid="cleanup-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 my-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-slate-500" />
            <h2 className="text-base font-bold text-slate-900">Clean up “{company.name}”</h2>
          </div>
          <button onClick={onClose} data-testid="cleanup-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {!dep && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Running dependency check…</div>}
          {dep && !dep.error && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Classification</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${cm?.tone}`} data-testid="cleanup-classification">{cm?.label}</span>
              </div>
              <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4 space-y-2 text-sm">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Dependency check</div>
                {Object.keys(dep.operational_refs || {}).length === 0 && dep.active_integrations.length === 0 && dep.configured_credentials.length === 0 ? (
                  <div className="flex items-center gap-1.5 text-emerald-600"><CheckCircle2 className="w-4 h-4" /> No operational data, integrations or credentials.</div>
                ) : (
                  <ul className="space-y-1 text-slate-600">
                    {Object.entries(dep.operational_refs || {}).map(([k, v]) => <li key={k}>• <span className="font-medium">{k}</span>: {v} record{v > 1 ? "s" : ""}</li>)}
                    {dep.active_integrations.length > 0 && <li>• Active integrations: {dep.active_integrations.join(", ")}</li>}
                    {dep.configured_credentials.length > 0 && <li>• Configured credentials: {dep.configured_credentials.join(", ")}</li>}
                  </ul>
                )}
                {Object.keys(dep.setup_refs || {}).length > 0 && (
                  <div className="text-xs text-slate-400 pt-1">Setup/history (preserved on archive): {Object.entries(dep.setup_refs).map(([k, v]) => `${k} (${v})`).join(", ")}</div>
                )}
              </div>

              {/* Archive / Restore */}
              {dep.archived ? (
                <button onClick={unarchive} disabled={busy} data-testid="cleanup-unarchive" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm px-4 py-2.5 disabled:opacity-50">
                  <RotateCcw className="w-4 h-4" /> Restore from archive
                </button>
              ) : (
                <button onClick={archive} disabled={busy} data-testid="cleanup-archive" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-white font-semibold text-sm px-4 py-2.5 disabled:opacity-50">
                  <Archive className="w-4 h-4" /> Archive company (recommended, non-destructive)
                </button>
              )}

              {/* Delete — strict */}
              <div className="rounded-xl ring-1 ring-rose-200 bg-rose-50/40 p-4">
                <div className="flex items-center gap-1.5 text-rose-700 font-semibold text-sm"><ShieldAlert className="w-4 h-4" /> Permanently delete</div>
                {dep.deletable ? (
                  <>
                    <p className="text-xs text-rose-600/90 mt-1">Only an empty Draft tenant can be deleted. Type <span className="font-mono font-semibold">{company.slug}</span> to confirm.</p>
                    <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="cleanup-confirm-slug" placeholder={company.slug}
                      className="mt-2 w-full bg-white border border-rose-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400/30" />
                    <button onClick={del} disabled={!canDelete || busy} data-testid="cleanup-delete" className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-semibold text-sm px-4 py-2.5 disabled:opacity-40">
                      <Trash2 className="w-4 h-4" /> Delete permanently
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-rose-600/90 mt-1">Blocked — this company has operational data, integrations or is not a plain Draft. Archive it instead.</p>
                )}
              </div>
            </>
          )}
          {dep && dep.error && <div className="text-sm text-rose-600">Could not load dependencies.</div>}
        </div>
      </div>
    </div>
  );
}

export default function PlatformOnboarding() {
  const [companies, setCompanies] = useState([]);
  const [meta, setMeta] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [cleanup, setCleanup] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get(`/platform/companies?include_archived=${showArchived}`).then((r) => setCompanies(r.data.companies)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);
  useEffect(() => { api.get("/platform/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900" data-testid="platform-onboarding-page">
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                <Rocket className="w-[18px] h-[18px] text-white" strokeWidth={2.2} />
              </span>
              <h1 className="text-2xl font-bold tracking-tight">Client Onboarding</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1.5 ml-0.5">Provision and configure tenant companies through their onboarding lifecycle.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowArchived((v) => !v)}
              data-testid="toggle-archived"
              className={`inline-flex items-center gap-1.5 rounded-lg text-sm font-semibold px-3.5 py-2.5 transition-colors ${showArchived ? "bg-slate-800 text-white" : "bg-white ring-1 ring-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              <Archive className="w-4 h-4" /> {showArchived ? "Showing archived" : "Archived"}
            </button>
            <button
              onClick={() => setShowAdd(true)}
              data-testid="add-company-button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm px-4 py-2.5 shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Company
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm overflow-hidden">
          <div className="hidden lg:grid grid-cols-[1.6fr_1fr_1.1fr_1.4fr_1.2fr_0.9fr_auto] gap-4 px-6 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <div>Company</div><div>Status</div><div>Setup</div><div>Enabled modules</div><div>Issues</div><div>Updated</div><div className="text-right">Action</div>
          </div>
          {companies.length === 0 && (
            <div className="px-6 py-16 text-center text-slate-400 text-sm" data-testid="onboarding-empty">No companies yet. Click “Add Company” to begin.</div>
          )}
          {companies.map((c) => {
            const live = c.onboarding_status === "live" && c.status === "active";
            const canDelete = c.onboarding_status === "draft" && c.status === "active" && !c.archived;
            return (
              <div
                key={c.id}
                data-testid={`onboarding-row-${c.slug}`}
                className={`grid grid-cols-1 lg:grid-cols-[1.6fr_1fr_1.1fr_1.4fr_1.2fr_0.9fr_auto] gap-3 lg:gap-4 px-6 py-4 border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors items-center ${c.archived ? "opacity-60" : ""}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 shrink-0 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-sm uppercase">
                    {(c.name || "?").slice(0, 2)}
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                      {c.name}
                      {c.archived && <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 text-[10px] font-semibold uppercase tracking-wide">Archived</span>}
                    </div>
                    <div className="text-xs text-slate-400 truncate">/{c.slug} · {c.member_count ?? 0} members</div>
                  </div>
                </div>
                <div><StatusPill company={c} /></div>
                <div><SetupBar pct={c.setup_pct} /></div>
                <div className="flex flex-wrap gap-1">
                  {(c.enabled_modules || []).slice(0, 4).map((m) => (
                    <span key={m} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium">{m}</span>
                  ))}
                  {(c.enabled_modules || []).length > 4 && (
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-medium">+{c.enabled_modules.length - 4}</span>
                  )}
                  {(c.enabled_modules || []).length === 0 && <span className="text-xs text-slate-300">None</span>}
                </div>
                <div>
                  {(c.issues || []).length === 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> All clear</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium" title={c.issues.join(", ")}>
                      <AlertTriangle className="w-3.5 h-3.5" /> {c.issues.length} issue{c.issues.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-400">{fmtDate(c.updated_at)}</div>
                <div className="flex items-center justify-end gap-1.5">
                  <button
                    onClick={() => navigate(`/platform/onboarding/${c.id}`)}
                    data-testid={`onboarding-setup-${c.slug}`}
                    className="inline-flex items-center gap-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1.5 transition-colors"
                  >
                    <Settings2 className="w-3.5 h-3.5" /> {live ? "View Setup" : "Continue Setup"}
                  </button>
                  <button
                    onClick={() => navigate(`/platform/companies/${c.id}`)}
                    data-testid={`onboarding-open-${c.slug}`}
                    title="Open Company"
                    className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold px-2.5 py-1.5 transition-colors"
                  >
                    <Building2 className="w-3.5 h-3.5" /> Open
                  </button>
                  <button
                    onClick={() => setCleanup(c)}
                    data-testid={`onboarding-cleanup-${c.slug}`}
                    title={canDelete ? "Archive or delete" : "Archive"}
                    className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 text-xs font-semibold px-2 py-1.5 transition-colors"
                  >
                    {c.archived ? <RotateCcw className="w-3.5 h-3.5" /> : canDelete ? <Trash2 className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showAdd && (
        <AddCompanyModal
          meta={meta}
          onClose={() => setShowAdd(false)}
          onCreated={(c) => { setShowAdd(false); load(); navigate(`/platform/onboarding/${c.id}`); }}
        />
      )}
      {cleanup && (
        <CleanupModal
          company={cleanup}
          onClose={() => setCleanup(null)}
          onDone={() => { setCleanup(null); load(); }}
        />
      )}
    </div>
  );
}
